import { useState } from 'react';
import { ImagePick } from '../components/ImagePick';
import { passes as Api, type Pass, type PassInput, type PubState } from '@tvx/api-client';
import { useAsync } from '../lib/useAsync';
import { Shim, SkeletonRows } from '../components/Shimmer';
import { useSelection, TickAll, Tick, BulkActions } from '../components/Bulk';
import { Modal } from '../components/Modal';
import { Field, Row, StateTag } from '../components/Field';

const BLANK: PassInput = {
  name: '', module: 'calls', price: 0, days: 30, note: '', features: '', featured: 0, sort: 0,
  state: 'draft', checkout_url: '',
};

/* One screen, two homes: the Plans item under News & Analysis and the one under
   Trade Calls. Each lists, and creates, only its own module's plans. */
const MODULES: Record<'calls' | 'news', { label: string; blurb: string }> = {
  calls: { label: 'Trade Calls', blurb: 'the trade calls desk' },
  news: { label: 'News & Analysis', blurb: 'the morning note and research' },
};

export function Passes({ module }: { module: 'calls' | 'news' }) {
  const { data, error, loading, reload } = useAsync((signal) => Api.list(signal));
  const [editing, setEditing] = useState<Pass | 'new' | null>(null);
  const mod = MODULES[module];
  const items = (data?.items ?? []).filter((p) => p.module === module);
  const live = items.filter((p) => p.state === 'published');

  const sel = useSelection(items);

  return (
    <div className="pg">
      <div className="rowbar">
        <div>
          <h1 style={{ fontSize: 29 }}>{mod.label} plans</h1>
          <p style={{ fontSize: 13.5, color: 'var(--tx2)', marginTop: 7 }}>
            {loading && !data ? <Shim w={260} h={12} style={{ marginTop: 3 }} />
              : live.length ? `${live.length} published — sold on the website for ${mod.blurb}`
              : `Nothing published yet. ${mod.label} cannot be bought until a plan is published.`}
          </p>
        </div>
        <button className="b bg" type="button" onClick={() => setEditing('new')}>New plan</button>
      </div>

      {error ? <div className="err">Could not load them — {error.message}</div> : null}

      <div className="crd" style={{ padding: '20px 22px' }}>
        <BulkActions
          path="/passes" noun="plan"
          rows={sel.picked.map((p) => ({ id: p.id, label: String(p.name) }))}
          actions={[['publish', 'Publish'], ['unpublish', 'Unpublish']]}
          onDone={async () => { sel.clear(); await reload(); }}
        />

        <div className="tblx">
          <table className="tbl">
            <thead>
              <tr>
                <TickAll on={sel.allOn} some={sel.picked.length > 0} onChange={sel.toggleAll} /><th>Plan</th><th>Price</th><th>Checkout</th><th>State</th><th /></tr>
            </thead>
            <tbody>
              {loading && !data ? (
                <SkeletonRows cols={6} tick />
              ) : items.length === 0 ? (
                <tr><td colSpan={6} className="muted" style={{ padding: '26px 8px' }}>
                  No plans yet. {mod.label} cannot be bought until one is published.
                </td></tr>
              ) : items.map((p) => (
                <tr key={p.id} style={sel.isOn(p.id) ? { background: 'rgba(var(--goldrgb), .05)' } : undefined}>
                  <Tick on={sel.isOn(p.id)} label={String(p.name)}
                    onChange={(v) => sel.toggle(p.id, v)} />
                  <td>
                    <div style={{ fontWeight: 700 }}>
                      {p.name}{p.featured ? <span className="tg" style={{ marginLeft: 8,
                        background: 'var(--goldsoft)', color: 'var(--goldtx)' }}>featured</span> : null}
                    </div>
                    {p.note ? <div style={{ fontSize: 11.5, color: 'var(--tx3)' }}>{p.note}</div> : null}
                  </td>
                  <td>
                    {p.price ? `₹${p.price.toLocaleString('en-IN')}` : <span className="muted">no price</span>}
                    <div style={{ fontSize: 11, color: 'var(--tx3)' }}>{p.days ? `for ${p.days} days` : 'no end date'}</div>
                  </td>
                  <td>
                    {p.checkout_url
                      ? <span style={{ color: 'var(--up)', fontSize: 12 }}>linked</span>
                      : <span style={{ color: 'var(--down)', fontSize: 12 }}>no link — cannot be bought</span>}
                  </td>
                  <td><StateTag state={p.state} /></td>
                  <td className="right">
                    <button className="b bo" style={{ padding: '6px 12px', fontSize: 11.5 }}
                      type="button" onClick={() => setEditing(p)}>Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing ? (
        <PassForm
          module={module}
          pass={editing === 'new' ? null : editing}
          onCancel={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await reload(); }}
        />
      ) : null}
    </div>
  );
}

function PassForm({ module, pass, onCancel, onSaved }: {
  module: 'calls' | 'news';
  pass: Pass | null;
  onCancel: () => void;
  onSaved: () => Promise<void>;
}) {
  const [f, setF] = useState<PassInput>(() => pass ? { ...pass } : { ...BLANK, module });
  const [cover, setCover] = useState<string | null>(pass?.cover_path ?? null);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const set = <K extends keyof PassInput>(k: K, v: PassInput[K]) => setF((p) => ({ ...p, [k]: v }));

  async function submit(state: PubState) {
    if (!f.name.trim()) { setError('A name is required.'); return; }
    if (!f.price) { setError('A price is required — a pass with no price cannot be bought.'); return; }
    if (f.checkout_url && !/^https:\/\//i.test(f.checkout_url)) {
      setError('The checkout link must start with https://.');
      return;
    }
    /* Without a link of its own a pass falls back to the default payment link
       in Settings; with neither, checkout can only tell people to get in touch. */
    setError('');
    setStatus(state === 'published' ? 'Publishing…' : 'Saving…');
    try {
      if (pass) await Api.update(pass.id, { ...f, state });
      else await Api.create({ ...f, state });
      await onSaved();
    } catch (e) { setStatus(''); setError((e as Error).message); }
  }

  return (
    <Modal
      title={pass ? `Edit · ${pass.name}` : 'New plan'}
      onClose={onCancel}
      footer={
        <>
          <span style={{ fontSize: 12, color: 'var(--tx3)' }}>{status}</span>
          <span className="mdl-spacer" />
          <button className="b bo" type="button" onClick={onCancel}>Cancel</button>
          <button className="b bo" type="button" onClick={() => void submit('draft')}>Save draft</button>
          <button className="b bg" type="button" onClick={() => void submit('published')}>
            {pass?.state === 'published' ? 'Save' : 'Publish'}
          </button>
        </>
      }
    >
      {error ? <div className="err">{error}</div> : null}

      <Row>
        <Field label="Name"><input className="inp" value={f.name} autoFocus
          onChange={(e) => set('name', e.target.value)} /></Field>
        <Field label="Price (₹)">
          <input className="inp" type="number" min={0} value={f.price || ''}
            onChange={(e) => set('price', Number(e.target.value))} /></Field>
        <Field label="Days of access" hint="0 means it never runs out.">
          <input className="inp" type="number" min={0} value={f.days === 0 ? '0' : f.days || ''}
            onChange={(e) => set('days', Number(e.target.value))} /></Field>
      </Row>

      <Field label="Note under the price"><input className="inp" value={f.note}
        onChange={(e) => set('note', e.target.value)} /></Field>

      <Field label="What it includes — one per line">
        <textarea className="inp ta" rows={5} value={f.features}
          onChange={(e) => set('features', e.target.value)} /></Field>

      {module === 'calls' ? (
        <Field label="Call Segment / Tier granted">
          <select className="inp" value={f.tier || 'all'} onChange={(e) => set('tier', e.target.value)}>
            <option value="all">All Trade Calls (Global)</option>
            <option value="equity">Equity Calls Only</option>
            <option value="fno">F&O Calls Only</option>
            <option value="forex">Forex Signals Only</option>
          </select>
        </Field>
      ) : null}

      <Field label="Checkout link"
        hint="Where the Pay button sends a buyer. Left empty, the default payment link in Settings is used.">
        <input className="inp" value={f.checkout_url} placeholder="https://cosmofeed.com/…"
          onChange={(e) => set('checkout_url', e.target.value)} /></Field>

      <div style={{ margin: '14px 0' }}>
        <ImagePick label="Plan picture" path={cover} alt={f.name}
          hint="Shown at the top of this plan wherever it is offered in the member area and the app. Until one is added a placeholder shows there. A 16:9 landscape fills the card exactly."
          onPick={(id, p) => { set('cover_media_id', id); setCover(p); }} />
      </div>

      <label style={{ display: 'flex', gap: 10, alignItems: 'center', cursor: 'pointer', marginTop: 6 }}>
        <input type="checkbox" checked={!!f.featured} onChange={(e) => set('featured', e.target.checked ? 1 : 0)} />
        <span style={{ fontSize: 13.5 }}>Mark as “most taken” on the website</span>
      </label>
    </Modal>
  );
}
